import { FC, useState, useEffect } from "react";
import { Box, InfoIcon, Tooltip, Typography } from "@kamstrup/kfl";
import { TextField } from "@kamstrup/kfl";
import { commonStyles } from "../../styles/commonStyles";
import * as styles from "./styles";
import { useTranslation } from "../../hooks/useTranslationHook/useTranslation";

export enum PasswordStrength {
  TooWeak = "TooWeak",
  Good = "Good",
  Strong = "Strong"
}

interface PasswordStrengthMeterProps {
  onStrengthChange: (strength: PasswordStrength) => void;
  onPasswordsMatch: (match: boolean) => void;
  onPasswordChange?: (password: string) => void;
  onLengthExceed?: (exceed: boolean) => void;
  onEnter?: () => void;
  passwordField: string;
  confirmPasswordField: string;
}

const MAX_LENGTH = 256;
const EMPTY_BAR_COLOR = "#D9D9D9";

const getStrengthLabel = (password: string, translateString: (key: any) => string) => {
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSymbol = /[@$!%*?&#]/.test(password);

  const conditionsMet = [hasUppercase, hasLowercase, hasNumber, hasSymbol].filter(Boolean).length;
  const isLongEnough = password.length >= 8;
  const isVeryLong = password.length >= 12;

  if (password.length === 0)
    return { label: "", color: EMPTY_BAR_COLOR, percentage: 0, strength: PasswordStrength.TooWeak };
  if (!isLongEnough)
    return {
      label: translateString("too_weak"),
      color: "#FF7A45",
      percentage: 33,
      strength: PasswordStrength.TooWeak
    };
  if (isVeryLong && conditionsMet === 4)
    return {
      label: translateString("strong"),
      color: "#52C41A",
      percentage: 100,
      strength: PasswordStrength.Strong
    };
  if (conditionsMet >= 3)
    return {
      label: translateString("good"),
      color: "#FFD666",
      percentage: 66,
      strength: PasswordStrength.Good
    };
  return {
    label: translateString("too_weak"),
    color: "#FF7A45",
    percentage: 33,
    strength: PasswordStrength.TooWeak
  };
};

export const PasswordStrengthMeter: FC<PasswordStrengthMeterProps> = ({
  onStrengthChange,
  onPasswordsMatch,
  onPasswordChange,
  onLengthExceed,
  onEnter,
  passwordField,
  confirmPasswordField
}) => {
  const { translateString } = useTranslation();
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");

  const passwordsMatch = newPassword === confirmPassword;
  const lengthExceeded = newPassword.length > MAX_LENGTH || confirmPassword.length > MAX_LENGTH;

  let { label, color, percentage, strength } = getStrengthLabel(newPassword, translateString);

  if (lengthExceeded) {
    label = "";
    color = EMPTY_BAR_COLOR;
    percentage = 0;
    strength = PasswordStrength.TooWeak;
  }

  useEffect(() => {
    onStrengthChange(strength);
    onPasswordsMatch(passwordsMatch);
  }, [strength, passwordsMatch, onStrengthChange, onPasswordsMatch]);

  useEffect(() => {
    onLengthExceed?.(lengthExceeded);
  }, [lengthExceeded, onLengthExceed]);

  const mismatchText =
    !passwordsMatch && confirmPassword.trim() !== "" ? translateString("password_not_match") : "";
  const lengthErrorText = translateString("password_exceeds_max_length");

  return (
    <Box>
      <Box sx={commonStyles.textFieldContainer}>
        <TextField
          name="new-password"
          size="large"
          label={passwordField}
          InputLabelProps={{ shrink: true }}
          type="password"
          autoComplete="new-password"
          fullWidth
          value={newPassword}
          onChange={(e) => {
            const pwd = e.target.value.trim();
            setNewPassword(pwd);
            onPasswordChange?.(pwd);
          }}
          error={Boolean(mismatchText) || lengthExceeded}
          helperText={lengthExceeded ? lengthErrorText : mismatchText}
        />
      </Box>

      <Box sx={commonStyles.textFieldContainer}>
        <TextField
          size="large"
          label={confirmPasswordField}
          InputLabelProps={{ shrink: true }}
          type="password"
          autoComplete="new-password"
          fullWidth
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value.trim())}
          error={Boolean(mismatchText) || lengthExceeded}
          helperText={lengthExceeded ? lengthErrorText : mismatchText}
        />
      </Box>

      <Box sx={styles.progressContainer}>
        <Box
          sx={{
            ...styles.progressBar,
            width: `${percentage}%`,
            backgroundColor: color
          }}
        />
      </Box>

      <Box sx={styles.strengthInfo}>
        <Typography variant="body4" sx={styles.strengthText}>
          {translateString("password_strength")} <span>{label}</span>
        </Typography>
        <Tooltip
          title={
            <Box>
              <Typography variant="body4">{translateString("password_tooltip_header")}</Typography>
              <ul>
                <li>
                  <Typography variant="body4">
                    {translateString("password_tooltip_length")}
                  </Typography>
                </li>
                <li>
                  <Typography variant="body4">
                    {translateString("password_tooltip_max_length")}
                  </Typography>
                </li>
                <li>
                  <Typography variant="body4">
                    {translateString("password_tooltip_rules")}
                  </Typography>
                  <ul>
                    <li>
                      <Typography variant="body4">
                        {translateString("password_tooltip_uppercase")}
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body4">
                        {translateString("password_tooltip_lowercase")}
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body4">
                        {translateString("password_tooltip_numbers")}
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body4">
                        {translateString("password_tooltip_symbols")}
                      </Typography>
                    </li>
                  </ul>
                </li>
              </ul>
            </Box>
          }
        >
          <InfoIcon data-testid="info-icon" sx={{ ...styles.infoIcon }} />
        </Tooltip>
      </Box>
    </Box>
  );
};
